import {
  CategoriesHelper,
  Category,
  DakenService,
  FitNetwork,
  FitRequirements,
  NumbersHelper,
  SuggestionRules,
  Tag,
  User,
  UserInDetails,
  onLangChange,
} from '@hub/shared'
import { SqLoader } from '@squidit/react-css'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  suggestionRules: SuggestionRules
  fitRequirements: FitRequirements
  userSelected: UserInDetails
  user: User
  onChange?: (value: any) => any
  onLoading?: (value: any) => any
}

export default ({ suggestionRules, fitRequirements, user, userSelected, onChange, onLoading, ...props }: Props) => {
  const { t, i18n } = useTranslation('perfilRequirements')
  const dakenService = useMemo(() => new DakenService(), [])
  const categoriesHelper = useMemo(() => new CategoriesHelper(), [])
  const numbersHelper = useMemo(() => new NumbersHelper(), [])
  const onLangChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [lang, setLang] = useState<string>('en')
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState<Category[]>([])
  const [socialNetworkObject, setSocialNetworkObject] = useState<any>([])
  const [profileCategories, setProfileCategories] = useState<number[]>([])
  const [userFit, setUserFit] = useState({})

  const requirements = useMemo(
    () => [
      {
        icon: 'fa-light fa-user-group',
        label: `${t('minFollowers')}`,
        value:
          !!suggestionRules?.minFollowers &&
          numbersHelper?.formatStandardNumber({ lang: i18n?.language, number: suggestionRules?.minFollowers }),
        key: 'minFollowers',
      },
      {
        icon: 'fa-light fa-users',
        label: `${t('maxFollowers')}`,
        value:
          suggestionRules?.maxFollowers &&
          numbersHelper?.formatStandardNumber({ lang: i18n?.language, number: suggestionRules?.maxFollowers }),
        key: 'maxFollowers',
      },
      {
        icon: 'fa-light fa-tag',
        label: `${t('categories')}`,
        value: suggestionRules?.categories,
        key: 'categories',
      },
    ],
    [t, suggestionRules, i18n?.language, numbersHelper]
  )

  const returnSubCategories = useCallback((categories: Category[]) => {
    let subCategories: Category[] = []
    for (const category of categories) {
      subCategories = subCategories.concat(category?.children)
    }
    return subCategories
  }, [])

  useEffect(() => {
    categoriesHelper.getCategories().then((allCategories) => {
      setCategories(allCategories?.concat(returnSubCategories(allCategories)))
    })
    onLangChangeSubscription.current = onLangChange.subscribe((lang) => {
      setLang(lang)
    })
    return () => {
      onLangChangeSubscription.current?.unsubscribe()
    }
  }, [categoriesHelper, returnSubCategories])

  useEffect(() => {
    if (userSelected?.idNetwork) {
      setLoading(true)
      setSocialNetworkObject(user?.[userSelected?.socialNetwork]?.find((e) => e.id === userSelected?.idNetwork))
      dakenService
        .getCategoriesBySocialNetwork(userSelected.idNetwork)
        .then((e) => {
          setProfileCategories(e?.concat(returnSubCategories(e))?.map((item: Category) => item?.id))
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [userSelected, user, dakenService, returnSubCategories])

  useEffect(() => {
    if (onLoading && typeof onLoading === 'function') {
      onLoading(loading)
    }
  }, [loading, onLoading])

  const getCategoryByLang = useCallback(
    (category?: Category) => {
      if (!category) {
        return null
      }
      if (lang.includes('pt')) {
        return category?.description
      }
      return category[`description_${lang}`]
    },
    [lang]
  )

  const renderCategory = (value: any) => {
    return value
      ?.map((category: number, index: number) =>
        categories?.find((c) => c?.id === category)
          ? `${index > 0 ? ' • ' : ''}${getCategoryByLang(categories?.find((c) => c?.id === category))}`
          : null
      )
      .join('')
  }

  const verifyRequirements = (field: string) => {
    const profileFit = fitRequirements?.fits?.fitNetwork?.filter(({ idNetwork }: FitNetwork) => idNetwork === userSelected?.idNetwork)
    if (
      (field === 'maxFollowers' && profileFit?.find((fitNetwork: FitNetwork) => fitNetwork?.rule === 'maxFollowers')?.fit) ||
      (field === 'minFollowers' && profileFit?.find((fitNetwork: FitNetwork) => fitNetwork?.rule === 'minFollowers')?.fit) ||
      (field === 'categories' && profileFit?.find((fitNetwork: FitNetwork) => fitNetwork?.rule === 'categories')?.fit)
    ) {
      if (userFit[field] === undefined || userFit[field] === false) {
        setUserFit({ ...userFit, [field]: true })
      }
      return 'fa-solid fa-circle-check'
    }
    if (userFit[field] === undefined || userFit[field] === true) {
      setUserFit({ ...userFit, [field]: false })
    }
    return 'fa-solid fa-circle-xmark'
  }

  const componentRule = useCallback(() => {
    return !suggestionRules?.minFollowers && !suggestionRules?.maxFollowers && suggestionRules?.categories?.length < 1
  }, [suggestionRules])

  useEffect(() => {
    if (onChange && typeof onChange === 'function' && !componentRule()) {
      onChange(Object.values(userFit).every((fieldValue) => fieldValue))
    }
  }, [userFit, onChange, componentRule])

  const verifyRender = (requirement: any) => {
    return (Array.isArray(requirement) && requirement?.length > 0) || (!Array.isArray(requirement) && requirement)
  }

  if (componentRule()) {
    return null
  }

  return (
    <section className="perfil-requirements" {...props}>
      <div className="title">
        <h5>{t('title')}</h5>
      </div>
      {requirements?.map((requirement: any) =>
        verifyRender(requirement?.value) ? (
          <Tag
            key={requirement.key}
            icon={
              <span className="icon-perfil-requirements">
                {loading ? (
                  <SqLoader size="small" />
                ) : (
                  <div>
                    <i className={`${requirement.icon}`} style={{ color: 'var(--text_color)' }}></i>
                    <div className="check">
                      <i
                        className={`${verifyRequirements(requirement.key)}`}
                        style={{
                          color: verifyRequirements(requirement.key) === 'fa-solid fa-circle-check' ? 'var(--green)' : 'var(--red)',
                        }}
                      ></i>
                    </div>
                  </div>
                )}
              </span>
            }
            labels={
              <p>
                {`${requirement.label}: `}
                <strong>{Array.isArray(requirement.value) ? renderCategory(requirement.value) : requirement.value}</strong>
              </p>
            }
          />
        ) : null
      )}
    </section>
  )
}
